// src/pages/DonationPage.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DonationHeroSection from '../components/Hero/DonationHeroSection';
import DonationTiers from '../components/Donation/DonationTiers';
import { ToastProvider } from '../contexts/ToastContext';
import { useCart } from '../contexts/CartContext';
import { fetchDonationCards } from '../api';
import './DonationPage.css';

// Main page component with donation functionality
const DonationPageContent = () => {
  const [tiers, setTiers] = useState([]);
  const { addToCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    let ignore = false;

    const loadCards = async () => {
      try {
        const data = await fetchDonationCards();
        if (!ignore) {
          setTiers(Array.isArray(data?.data) ? data.data : []);
        }
      } catch (error) {
        console.error('Failed to load donation cards from API, using fallback cards:', error);
      }
    };

    loadCards();

    return () => {
      ignore = true;
    };
  }, []);

  const handleAddToCart = (tier) => {
    if (tier.frequency === 'monthly' || tier.type === 'subscription') {
      navigate('/register?plan=monthly');
      return { destination: 'membership' };
    }

    const selectedOptions = tier.selectedOptions || {};
    const optionKey = Object.values(selectedOptions).filter(Boolean).join('-');
    const donationId = tier.db_id || tier.id || tier.slug;

    addToCart({
      id: `donation-${donationId}${optionKey ? `-${optionKey}` : ''}`,
      itemType: 'donation',
      donationCardId: tier.db_id || null,
      donationCardSlug: tier.slug || tier.id,
      name: tier.displayName || tier.name,
      price: Number(tier.price) || 0,
      image: tier.image_url || null,
      quantity: tier.quantity || 1,
      frequency: 'one-time',
      donationType: tier.type || 'donation',
      shippingRequired: Boolean(tier.shippingRequired),
      taxable: tier.type === 'merchandise',
      variant: Object.keys(selectedOptions).length > 0
        ? { size: selectedOptions.size || null }
        : null,
      selectedOptions,
    });

    return { destination: 'cart' };
  };


  return (
    <div className="donation-page">
      {/* Donation/Sponsorship Hero Section */}
      <DonationHeroSection />
      
      <div className="donation-container">

        {/* Put the action directly after the hero; supporting context follows
            after visitors can see the available choices. */}
        <section
          id="donation-tiers"
          className="donation-section donation-tiers-section"
        >
          <DonationTiers onAddToCart={handleAddToCart} incomingTiers={tiers} />
        </section>

        {/* Main Content */}
        <section 
          id="donation-content" 
          className="donation-section content-section"
        >
          <div className="content-text">
            <h2>Why Your Support Matters</h2>
            <p>
              <em>Guhso</em> isn't just a podcast—it's a movement celebrating Jamaican culture, 
              authentic conversations, and real connections. Your bigup helps us:
            </p>
            <ul>
              <li>Keep producing weekly episodes with no corporate filters</li>
              <li>Maintain our independence and authentic voice</li>
              <li>Expand our reach to bring Yardie vibes worldwide</li>
              <li>Cover production costs and support our growing team</li>
              <li>Create more content like our blog and tech coverage</li>
            </ul>
            <p>
              Every contribution, no matter the size, shows love for the culture 
              and helps us continue growing this community of authentic voices.
            </p>
          </div>
        </section>

        {/* Contact Section */}
        <section 
          id="donation-contact" 
          className="donation-section contact-section"
        >
          <div className="contact-content">
            <h2>Get in Touch</h2>
            <p>Want to support in other ways? Have ideas? Let's talk!</p>
            <div className="contact-info">
              <p>
                <i className="fas fa-envelope"></i>
                <a href="mailto:chatbout@guhso.com">chatbout@guhso.com</a>
              </p>
              <p>
                <i className="fas fa-phone"></i>
                <a href="tel:+17543008618">(754) 300-8618</a>
              </p>
            </div>
          </div>
        </section>
      </div>

    </div>
  );
};

// Main component wrapped with providers
const DonationPage = () => {
  return (
    <ToastProvider>
      <DonationPageContent />
    </ToastProvider>
  );
};

export default DonationPage;
